import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Table, Spinner, Button, Input, Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from "reactstrap";
import { toast } from "react-toastify";
import Pagination from "../../Components/Paginate";
import { FaHistory, FaEye, FaDownload, FaFilter, FaCalendarAlt, FaMapMarkerAlt, FaCheckCircle, FaTicketAlt, FaEllipsisV } from "react-icons/fa";
import { fetchTickets, fetchSpots } from "../../redux/actions/TicketActions";

const TicketHistory = () => {
  const dispatch = useDispatch();
  const {
    TicketReducer: { tickets: history, count: historyCount, spots, loading },
  } = useSelector((state) => state);

  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("desc");
  const [paginate, setPaginate] = useState(10);
  const [ticketType, setTicketType] = useState("all");
  const [status, setStatus] = useState("all");
  const [dateRange, setDateRange] = useState("all");
  const [place, setPlace] = useState("all");
  const [showCustomRange, setShowCustomRange] = useState(false);
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
  const [actionDropdowns, setActionDropdowns] = useState({});

  useEffect(() => {
    const params = {
      page: page + 1,
      limit: paginate,
      search,
      sort
    };

    if (ticketType !== "all") {
      params.ticket_booking_type = ticketType;
    }

    if (status !== "all") {
      if (status === "active") params.is_valid = 1;
      else if (status === "used") params.is_used = 1;
      else if (status === "expired") params.is_valid = 0;
    }

    if (place !== "all") params.place = place;

    if (dateRange !== "all") {
      if (dateRange === "today") {
        const today = new Date().toISOString().split('T')[0];
        params.start_date = today;
        params.end_date = today;
      } else if (dateRange === "week") {
        const today = new Date();
        const weekStart = new Date(today.getFullYear(), today.getMonth(), today.getDate() - today.getDay());
        params.start_date = weekStart.toISOString().split('T')[0];
        params.end_date = new Date().toISOString().split('T')[0];
      } else if (dateRange === "month") {
        const today = new Date();
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        params.start_date = monthStart.toISOString().split('T')[0];
        params.end_date = new Date().toISOString().split('T')[0];
      } else if (dateRange === "custom" && customStartDate && customEndDate) {
        params.start_date = customStartDate;
        params.end_date = customEndDate;
      }
    }

    dispatch(fetchTickets(params));
  }, [dispatch, paginate, page, search, sort, ticketType, status, dateRange, place, customStartDate, customEndDate]);

  useEffect(() => {
    dispatch(fetchSpots());
  }, [dispatch]);



  const clearFilters = () => {
    setSearch("");
    setTicketType("all");
    setStatus("all");
    setDateRange("all");
    setPlace("all");
    setShowCustomRange(false);
    setCustomStartDate("");
    setCustomEndDate("");
  };

  const handleExportCSV = () => {
    if (!history?.length) return;
    const csvData = history.map((ticket, index) => ({
      "SL. No.": index + 1,
      "Ticket ID": ticket.ticket_id,
      "Place Name": ticket.place?.place_name,
      "Visitor Name": ticket.visitor_name,
      "Mobile": ticket.visitor_phone || ticket.mobile,
      "Booking Date": new Date(ticket.createdAt).toLocaleDateString(),
      "Visit Date": new Date(ticket.booking_date).toLocaleDateString(),
      "Type": ticket.ticket_booking_type,
      "Status": ticket.is_used === 1 ? 'Used' : ticket.is_valid === 1 ? 'Active' : 'Expired',
      "Amount": `₹${ticket.total_amount}`
    }));
    
    const csv = [Object.keys(csvData[0]).join(','), ...csvData.map(row => Object.values(row).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'ticket-history.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleExportPDF = () => {
    toast.info("PDF export functionality will be implemented soon");
  };

  return (
    <div>
      <div className="row my-3">
        <div className="col-md-12">
          <div className="mb-4 d-flex align-items-center justify-content-between">
            <h5 className="mb-0 text-white fs-4">
              <FaHistory className="me-2" />
              Ticket History
            </h5>
          </div>
        </div>
      </div>

      <div
        className="bg-white px-3 py-4 rounded-2 my-4"
        style={{ boxShadow: "4px 4px 18px rgba(0,0,0,0.15)" }}
      >
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <div className="flex-grow-1" style={{ maxWidth: "190px" }}>
            <Input
              type="text"
              className="form-control"
              placeholder="🔍 Search by ID, Visitor, Phone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="d-flex flex-wrap gap-2">
            <div className="position-relative">
              <FaTicketAlt className="position-absolute" style={{ left: '8px', top: '50%', transform: 'translateY(-50%)', zIndex: 1, color: '#6c757d' }} />
              <Input
                type="select"
                value={ticketType}
                onChange={(e) => setTicketType(e.target.value)}
                style={{ width: "140px", paddingLeft: '30px' }}
              >
                <option value="all">All Types</option>
                <option value="online">Online</option>
                <option value="counter">Counter</option>
              </Input>
            </div>
            <div className="position-relative">
              <FaCheckCircle className="position-absolute" style={{ left: '8px', top: '50%', transform: 'translateY(-50%)', zIndex: 1, color: '#6c757d' }} />
              <Input
                type="select"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                style={{ width: "140px", paddingLeft: '30px' }}
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="used">Used</option>
                <option value="expired">Expired</option>
              </Input>
            </div>
            <div className="position-relative">
              <FaMapMarkerAlt className="position-absolute" style={{ left: '8px', top: '50%', transform: 'translateY(-50%)', zIndex: 1, color: '#6c757d' }} />
              <Input
                type="select"
                value={place}
                onChange={(e) => setPlace(e.target.value)}
                style={{ width: "140px", paddingLeft: '30px' }}
              >
                <option value="all">All Spots</option>
                {spots.map((spot) => (
                  <option key={spot} value={spot}>
                    {spot}
                  </option>
                ))}
              </Input>
            </div>
            <div className="position-relative">
              <FaCalendarAlt className="position-absolute" style={{ left: '8px', top: '50%', transform: 'translateY(-50%)', zIndex: 1, color: '#6c757d' }} />
              <Input
                type="select"
                value={dateRange}
                onChange={(e) => {
                  setDateRange(e.target.value);
                  setShowCustomRange(e.target.value === "custom");
                }}
                style={{ width: "140px", paddingLeft: '30px' }}
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
                <option value="custom">Custom Range</option>
              </Input>
            </div>
            <Dropdown
              isOpen={exportDropdownOpen}
              toggle={() => setExportDropdownOpen(!exportDropdownOpen)}
            >
              <DropdownToggle className="btn btn-outline-secondary text-white">
                <FaDownload className="me-1" />
                Export
              </DropdownToggle>
              <DropdownMenu>
                <DropdownItem onClick={handleExportCSV}>
                  Export CSV
                </DropdownItem>
                <DropdownItem onClick={handleExportPDF}>
                  Export PDF
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
            <button
              className="btn btn-outline-danger"
              onClick={clearFilters}
            >
              <FaFilter className="me-1" />
              Clear Filters
            </button>
          </div>
        </div>
        {showCustomRange && (
          <div className="d-flex align-items-center gap-2 mb-3 p-3 bg-light rounded">
            <label className="fw-bold mb-0">From:</label>
            <Input
              type="date"
              value={customStartDate}
              onChange={(e) => setCustomStartDate(e.target.value)}
              style={{ width: "150px" }}
            />
            <label className="fw-bold mb-0">To:</label>
            <Input
              type="date"
              value={customEndDate}
              onChange={(e) => setCustomEndDate(e.target.value)}
              style={{ width: "150px" }}
            />
          </div>
        )}
        <div className="row">
          <div className="col-md-12">
            <Table hover responsive>
              <thead>
                <tr>
                  <th>SL. No.</th>
                  <th>Ticket ID</th>
                  <th>Place Name</th>
                  <th>Visitor Name</th>
                  <th>Mobile</th>
                  <th>Booking Date</th>
                  <th>Visit Date</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Amount</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="11" className="text-center">
                      <Spinner />
                    </td>
                  </tr>
                ) : history.length === 0 ? (
                  <tr>
                    <td colSpan="11" className="text-center">No data found</td>
                  </tr>
                ) : (
                  history.map((ticket, index) => (
                    <tr key={ticket.id}>
                      <th scope="row">{index + 1 + page * paginate}</th>
                      <td>{ticket.ticket_id}</td>
                      <td>{ticket.place?.place_name}</td>
                      <td>{ticket.visitor_name}</td>
                      <td>{ticket.visitor_phone || ticket.mobile}</td>
                      <td>{new Date(ticket.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })} {new Date(ticket.createdAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}</td>
                      <td>{new Date(ticket.booking_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                      <td>
                        <span className={`badge ${ticket.ticket_booking_type === 'online' ? 'bg-primary' : 'bg-secondary'}`}>
                          {ticket.ticket_booking_type}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${ticket.is_used === 1 ? 'bg-success' : ticket.is_valid === 1 ? 'bg-warning' : 'bg-danger'}`}>
                          {ticket.is_used === 1 ? 'Used' : ticket.is_valid === 1 ? 'Active' : 'Expired'}
                        </span>
                      </td>
                      <td>₹{ticket.total_amount}</td>
                      <td>
                        <Dropdown
                          isOpen={actionDropdowns[ticket.id] || false}
                          toggle={() => setActionDropdowns(prev => ({ ...prev, [ticket.id]: !prev[ticket.id] }))}
                        >
                          <DropdownToggle className="btn btn-sm btn-outline-secondary">
                            <FaEllipsisV style={{ color: 'white' }} />
                          </DropdownToggle>
                          <DropdownMenu>
                            <DropdownItem>
                              <FaEye className="me-2" /> View
                            </DropdownItem>
                          </DropdownMenu>
                        </Dropdown>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </div>
        <div className="row align-items-center">
          <div className="col-md-6 d-flex">
            <div className="col-md-2">
              <Input
                type="select"
                id="paginate"
                onChange={(e) => setPaginate(e.target.value)}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </Input>
            </div>
          </div>
          <div className="col-md-6">
            <Pagination
              totalItems={historyCount}
              itemsPerPage={paginate}
              currentPage={page}
              onPageChange={setPage}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TicketHistory;